import { User } from '@betterfunnel/types';
import * as Sentry from '@sentry/react';
import {
  ActionCodeSettings,
  Auth,
  User as FirebaseUser,
  getAuth,
  GoogleAuthProvider,
  OAuthProvider,
  onAuthStateChanged,
  SAMLAuthProvider,
  sendSignInLinkToEmail,
  signInWithEmailLink,
  signInWithPopup,
  signOut,
} from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import mixpanel from 'mixpanel-browser';
import { createContext, useEffect, useMemo, useState } from 'react';
import { auth, db } from '../Firebase';
import { usePersistedJsonState } from '../hooks/usePersistedState';
import { getSSOAuthProvider as getSSOAuthProviderApi } from '../services/AccountsService';
import { emptyConverter } from '../utils';
import { updateHotjar } from '../utils/hotjar';
import { updateMixpanel } from '../utils/mixpanel';

interface AuthContextProps {
  user?: User | null;
  firebaseUser?: FirebaseUser;
  isAuthenticated: boolean;
  isAllowed: boolean;
  isSupportAdmin: boolean;
  logout: () => void;
  loginWithGoogle: () => Promise<any>;
  loginWithMicrosoft: () => Promise<any>;
  loginWithEmailLink: (auth: Auth, email: string, href: string) => Promise<any>;
  sendLoginLinkToEmail: (email: string) => Promise<any>;
  loginWithSAML: (providerId: string) => Promise<any>;
  getSSOAuthProvider: (email: string) => Promise<any>;
}

export const AuthContext = createContext<AuthContextProps>({
  logout: () => {},
  loginWithGoogle: async () => {},
  loginWithMicrosoft: async () => {},
  loginWithEmailLink: async () => {},
  sendLoginLinkToEmail: async () => {},
  loginWithSAML: async () => {},
  getSSOAuthProvider: async () => {},
  isAuthenticated: false,
  isAllowed: false,
  isSupportAdmin: false,
});

export function AuthContextProvider(props: any) {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser>();
  const [user, setUser] = usePersistedJsonState<User | undefined | null>(
    'user',
  );
  const [allowedUser, setAllowedUser] = useState<boolean | undefined>();
  const [isAuthenticated, setIsAuthenticated] =
    usePersistedJsonState<boolean>('isAuthenticated');

  const isSupportAdmin = useMemo(() => user?.isSupportAdmin ?? false, [user]);

  const isAllowed = useMemo(() => {
    if (!firebaseUser) return true;
    if (allowedUser === false) return false;

    return true;
  }, [firebaseUser, allowedUser]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setFirebaseUser(user || undefined);
      setIsAuthenticated(!!user);
      updateMixpanel(user || undefined);
      updateHotjar(user);
    });

    return () => {
      unsubscribe();
    };
  }, [setIsAuthenticated]);

  useEffect(() => {
    Sentry.setUser(
      firebaseUser
        ? {
            id: firebaseUser.uid,
            ...(firebaseUser.email ? { email: firebaseUser.email } : {}),
          }
        : null,
    );
  }, [firebaseUser]);

  useEffect(() => {
    if (!firebaseUser) {
      setUser(undefined);
      return;
    }

    const unsubscribe = onSnapshot(
      doc(db, 'users', firebaseUser.uid).withConverter(emptyConverter<User>()),
      async (user) => {
        setUser(user.data() || null);
      },
    );

    return () => {
      unsubscribe();
    };
  }, [firebaseUser, setUser]);

  useEffect(() => {
    if (!firebaseUser) {
      setAllowedUser(undefined);
      return;
    }

    const unsubscribe = onSnapshot(
      doc(db, 'allowedUsers', firebaseUser.email ?? 'nothing'),
      (allowedUser) => {
        setAllowedUser(allowedUser.exists());
      },
    );

    return () => {
      unsubscribe();
    };
  }, [firebaseUser]);

  const loginWithGoogle = (): Promise<any> => {
    const provider = new GoogleAuthProvider();
    auth.useDeviceLanguage();
    return signInWithPopup(auth, provider);
  };

  const loginWithMicrosoft = (): Promise<any> => {
    const provider = new OAuthProvider('microsoft.com');
    provider.setCustomParameters({
      // Force re-consent.
      prompt: 'consent',
      // Target specific email with login hint.
      // login_hint: 'user@firstadd.onmicrosoft.com'
    });
    provider.addScope('openid');
    provider.addScope('email');
    auth.useDeviceLanguage();
    return signInWithPopup(auth, provider);
  };

  const loginWithSAML = (providerId: string): Promise<any> => {
    const _provider = new SAMLAuthProvider(providerId);
    auth.useDeviceLanguage();

    return signInWithPopup(auth, _provider).then((result) => {
      console.log('result', result);
      return result;
    });
  };

  const loginWithEmailLink = (
    auth: Auth,
    email: string,
    href: string,
  ): Promise<any> => {
    return signInWithEmailLink(auth, email, href).then(() => {
      // Clear email from storage.
      window.localStorage.removeItem('emailForSignIn');
    });
  };

  const sendLoginLinkToEmail = (email: string): Promise<any> => {
    const auth = getAuth();
    const settings: ActionCodeSettings = {
      url: 'https://app.betterfunnel.ai/login',
      handleCodeInApp: true,
    };
    return sendSignInLinkToEmail(auth, email, settings);
  };

  const getSSOAuthProvider = async (email: string): Promise<any> => {
    return getSSOAuthProviderApi(email);
  };

  const logout = () => {
    signOut(auth);
    setUser(undefined);
    setFirebaseUser(undefined);
    setIsAuthenticated(false);
    mixpanel.reset();
  };

  return (
    <AuthContext.Provider
      value={{
        user: user,
        firebaseUser,
        isAuthenticated,
        isAllowed,
        isSupportAdmin: isSupportAdmin,
        loginWithGoogle,
        loginWithMicrosoft,
        loginWithEmailLink,
        loginWithSAML,
        sendLoginLinkToEmail,
        logout,
        getSSOAuthProvider,
      }}
    >
      {props.children}
    </AuthContext.Provider>
  );
}
